/**
 * Seed Admin — API client
 */

const API_BASE =
  process.env.REACT_APP_API_URL || 'https://naturalseed.app';

function getToken() {
  return localStorage.getItem('seed_admin_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  const data = await res.json();

  if (!res.ok) {
    // If the token is expired or invalid, clear the session and redirect to login
    if (res.status === 401 && path !== '/api/admin/login') {
      localStorage.removeItem('seed_admin_token');
      localStorage.removeItem('seed_admin_user');
      window.location.href = '/login';
      return;
    }
    throw new Error(data.error || `Request failed (${res.status})`);
  }

  return data;
}

// Auth
export const adminLogin = (email, password) =>
  request('/api/admin/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

// Stats
export const getStats = () => request('/api/admin/stats');

// Users
export const getUsers = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/users${qs ? `?${qs}` : ''}`);
};

export const updateUser = (id, body) =>
  request(`/api/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// Emails
export const getEmails = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/emails${qs ? `?${qs}` : ''}`);
};

export const getEmail = (id) => request(`/api/admin/emails/${id}`);

export const updateEmail = (id, body) =>
  request(`/api/admin/emails/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const replyToEmail = (id, replyBody, fromAddress) =>
  request(`/api/admin/emails/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify({ replyBody, fromAddress }),
  });

// Posts
export const getPosts = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/posts${qs ? `?${qs}` : ''}`);
};

export const moderatePost = (id, isActive) =>
  request(`/api/admin/posts/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ isActive }),
  });

export const deletePost = (id) =>
  request(`/api/admin/posts/${id}`, { method: 'DELETE' });

export const createCommunityPost = (body) =>
  request('/api/admin/posts', { method: 'POST', body: JSON.stringify(body) });

// Phase 2: Audit logs
export const getAuditLogs = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/audit-logs${qs ? `?${qs}` : ''}`);
};

// Phase 2: Security
export const getSecurity = () => request('/api/admin/security');

// Phase 2: Analytics
export const getAnalytics = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/analytics${qs ? `?${qs}` : ''}`);
};

// Phase 2: API usage
export const getApiUsage = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/api-usage${qs ? `?${qs}` : ''}`);
};

// Phase 3: Content management
export const getContent = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/content${qs ? `?${qs}` : ''}`);
};

export const createContent = (body) =>
  request('/api/admin/content', { method: 'POST', body: JSON.stringify(body) });

export const updateContent = (id, body) =>
  request(`/api/admin/content/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const deleteContent = (id) =>
  request(`/api/admin/content/${id}`, { method: 'DELETE' });

export const generateAIDraft = (body) =>
  request('/api/admin/content/ai-draft', { method: 'POST', body: JSON.stringify(body) });

// Phase 3: Notifications
export const getNotifications = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/notifications${qs ? `?${qs}` : ''}`);
};

export const createNotification = (body) =>
  request('/api/admin/notifications', { method: 'POST', body: JSON.stringify(body) });

export const updateNotification = (id, body) =>
  request(`/api/admin/notifications/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const sendNotification = (id) =>
  request(`/api/admin/notifications/${id}/send`, { method: 'POST' });

// Phase 3: Support tickets
export const getTickets = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/tickets${qs ? `?${qs}` : ''}`);
};

export const createTicket = (body) =>
  request('/api/admin/tickets', { method: 'POST', body: JSON.stringify(body) });

export const updateTicket = (id, body) =>
  request(`/api/admin/tickets/${id}`, { method: 'PUT', body: JSON.stringify(body) });

// Agents
export const getAgentTasks = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/agents/tasks${qs ? `?${qs}` : ''}`);
};

export const getAgentTask = (id) => request(`/api/admin/agents/tasks/${id}`);

export const updateAgentTask = (id, body) =>
  request(`/api/admin/agents/tasks/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const executeAgentTask = (id) =>
  request(`/api/admin/agents/tasks/${id}/execute`, { method: 'POST' });

export const getAgentRuns = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/agents/runs${qs ? `?${qs}` : ''}`);
};

export const getAgentStats = () => request('/api/admin/agents/stats');

// Content Studio — Skill Graph
export const getSkillGraphNodes = () =>
  request('/api/admin/content-engine/skill-graph');

export const updateSkillGraphNode = (id, body) =>
  request(`/api/admin/content-engine/skill-graph/${id}`, { method: 'PUT', body: JSON.stringify(body) });

// Content Studio — Topics
export const getContentTopics = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/content-engine/topics${qs ? `?${qs}` : ''}`);
};

export const createContentTopic = (body) =>
  request('/api/admin/content-engine/topics', { method: 'POST', body: JSON.stringify(body) });

export const updateContentTopic = (id, body) =>
  request(`/api/admin/content-engine/topics/${id}`, { method: 'PUT', body: JSON.stringify(body) });

// Content Studio — Drafts
export const getContentDrafts = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/admin/content-engine/drafts${qs ? `?${qs}` : ''}`);
};

export const updateContentDraft = (id, body) =>
  request(`/api/admin/content-engine/drafts/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const publishContentDraft = (id) =>
  request(`/api/admin/content-engine/drafts/${id}/publish`, { method: 'POST' });

export const generateDraftsForTopic = (topicId) =>
  request(`/api/admin/content-engine/generate/${topicId}`, { method: 'POST' });

// Content Studio — Social Accounts
export const getSocialAccounts = () =>
  request('/api/admin/content-engine/social-accounts');

export const upsertSocialAccount = (platform, body) =>
  request(`/api/admin/content-engine/social-accounts/${platform}`, { method: 'PUT', body: JSON.stringify(body) });
